import { Plus, Trash2 } from 'lucide-react';
import { StorageToolbarMenuItem } from '../../toolbar/StorageToolbarMenuItem';
import { registerStorageToolbarExtension, type StorageToolbarContext } from '../../toolbar/toolbarExtensions';
import { useTableAdminRefusal } from '../model/tableAdminRefusals';
import { STORAGE_READ_ONLY_REASON } from '../../../../../core/engines/azureStorageRefusal';
import { trackStorageEvent } from '../../services/storageAnalytics';
import { openStorageExplorerTab } from '../../utils/openStorageExplorerTab';
import { CreateStorageTableDialog } from '../components/CreateStorageTableDialog';
import { DeleteStorageTableDialog } from '../components/DeleteStorageTableDialog';

const SIBLING_REASON = 'This attachment cannot create sibling tables.';

/** S2's context: the host tab id and S4's scope capability travel in the `capabilities` record. */
const canCreateTable = (context: StorageToolbarContext): boolean => context.capabilities.canCreateTable !== false;

/**
 * Round B U23: creating ANOTHER table is an account-level action, so on a table's own tab it lives in More (the tree's
 * Tables folder keeps "Create table…"), not as a primary button on a row of its own. A9: both entries are menu items
 * (keyboard-navigable) that stay pointer-enabled with the reason when unavailable — C2: the storage More menus' item,
 * so the reason is a visible second line and Delete table… reads destructive.
 */
function CreateTableItem({ context }: { context: StorageToolbarContext }) {
  // C2 R10: after the read-only and scope rules, Azure's own refusal of tables/write once it is known — never the
  // entity-read refusal (a different permission). The reason shows as the item's second line.
  const refusal = useTableAdminRefusal(context.connectionId, { op: 'create' });
  const reason = context.readOnly ? STORAGE_READ_ONLY_REASON : !canCreateTable(context) ? SIBLING_REASON : refusal;
  const close = (open: boolean) => { if (!open) context.openDialog(null); };
  return (
    <StorageToolbarMenuItem testId="storage-toolbar-create-table" icon={Plus} label="Create table…" reason={reason} onSelect={() => {
      trackStorageEvent('feature:storage_table_admin', { action: 'open_create' });
      context.openDialog(
        <CreateStorageTableDialog
          open
          connectionId={context.connectionId}
          onOpenChange={close}
          onCreated={(table) => openStorageExplorerTab({ connectionId: context.connectionId, root: { kind: 'table', table }, source: 'tree' })}
        />,
      );
    }} />
  );
}

function DeleteTableItem({ context }: { context: StorageToolbarContext }) {
  const table = context.resource.kind === 'table' ? context.resource.table : undefined;
  // C2-01: this table's own tables/delete refusal — another table's does not apply (roles can be table-scoped).
  const refusal = useTableAdminRefusal(context.connectionId, table === undefined ? undefined : { op: 'delete', table });
  const reason = context.readOnly ? STORAGE_READ_ONLY_REASON : refusal;
  if (!table) return null;
  const close = (open: boolean) => { if (!open) context.openDialog(null); };
  return (
    <StorageToolbarMenuItem testId="storage-toolbar-delete-table" icon={Trash2} label="Delete table…" destructive reason={reason} onSelect={() => {
      trackStorageEvent('feature:storage_table_admin', { action: 'open_delete' });
      context.openDialog(<DeleteStorageTableDialog open connectionId={context.connectionId} table={table} onOpenChange={close} onDeleted={() => context.openDialog(null)} />);
    }} />
  );
}

let registered = false;
/** S4 toolbar entries (orders 300–399) in S2's shared registry; the host renders the dialog node they hand to `openDialog`. */
export function registerS4TableToolbarExtensions(): void {
  if (registered) return;
  registered = true;
  registerStorageToolbarExtension({
    id: 's4-create-table', placement: 'more', order: 300,
    isVisible: (context) => context.mode === 'entities',
    render: (context) => <CreateTableItem context={context} />,
  });
  registerStorageToolbarExtension({
    id: 's4-delete-table', placement: 'more', order: 310,
    isVisible: (context) => context.mode === 'entities' && context.resource.kind === 'table',
    render: (context) => <DeleteTableItem context={context} />,
  });
}
